import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LucideIconsModule } from '@shared/lucide-icons.module';

import { CrisisCardComponent } from './message-types/crisis-card.component';
import { CommunityJoinCardComponent } from './message-types/community-join-card.component';
import { PeerCardComponent } from './message-types/peer-card.component';
import { PostResultCardComponent } from './message-types/post-result-card.component';
import {
  ChatMessage,
  ChatAction,
  ChatSuggestion,
  FeedbackSentiment,
  GuidedEntryMode
} from './health-nav.models';

@Component({
  selector: 'app-health-nav-chat',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideIconsModule,
    CrisisCardComponent,
    CommunityJoinCardComponent,
    PeerCardComponent,
    PostResultCardComponent
  ],
  templateUrl: './health-nav-chat.component.html',
  styleUrls: ['./health-nav-chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HealthNavChatComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() messages: ChatMessage[] = [];
  @Input() assistantAvailable = true;
  @Input() isStreaming = false;
  @Input() errorMessage = '';
  @Input() noticeTone: 'info' | 'warning' | 'error' = 'error';
  @Input() chips: ChatSuggestion[] = [];
  @Input() contextualActions: ChatAction[] = [];
  @Input() contextualActionMessageId: string | null = null;
  @Input() withBottomNav = false;
  @Input() guidedMode: GuidedEntryMode | null = null;
  @Input() loadingLabel = 'Thinking...';
  @Input() inputPlaceholder = 'Ask about communities, posts, or share what is on your mind...';
  @Input() inputHelperText = '';
  @Input() draftText = '';
  @Input() focusRequest = 0;

  @Output() inputTextChange = new EventEmitter<string>();
  @Output() sendMessage = new EventEmitter<string>();
  @Output() close = new EventEmitter<void>();
  @Output() chipSelected = new EventEmitter<ChatSuggestion | string>();
  @Output() feedback = new EventEmitter<{ messageId: string; sentiment: FeedbackSentiment }>();
  @Output() useDraft = new EventEmitter<string>();
  @Output() refineDraft = new EventEmitter<string>();
  @Output() joinCommunity = new EventEmitter<string>();
  @Output() showMoreResults = new EventEmitter<string>();
  @Output() navigateAction = new EventEmitter<ChatAction>();
  @Output() clearGuidedMode = new EventEmitter<void>();
  @Output() retry = new EventEmitter<void>();
  @Output() newChat = new EventEmitter<void>();

  @ViewChild('messagesEl') private readonly messagesEl?: ElementRef<HTMLElement>;
  @ViewChild('inputEl') private readonly inputEl?: ElementRef<HTMLTextAreaElement>;

  draftValue = '';
  suggestionsExpanded = true;
  copiedMessageId: string | null = null;
  showIdleNudge = false;
  private idleNudgeTimer?: ReturnType<typeof setTimeout>;

  ngAfterViewInit(): void {
    this.adjustInputHeight();
    this.scheduleIdleNudge();
  }

  ngOnDestroy(): void {
    this.clearIdleNudgeTimer();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['draftText']) {
      this.draftValue = changes['draftText'].currentValue ?? '';
      queueMicrotask(() => this.adjustInputHeight());
    }

    if (changes['messages']) {
      this.syncSuggestionVisibility();
      this.scheduleIdleNudge();
      queueMicrotask(() => this.scrollToBottom());
    }

    if (changes['focusRequest']) {
      queueMicrotask(() => this.focusInput());
    }

    if (changes['chips'] || changes['assistantAvailable']) {
      this.scheduleIdleNudge();
      queueMicrotask(() => this.adjustInputHeight());
    }
  }

  handleInput(value: string): void {
    this.draftValue = value;
    this.showIdleNudge = false;
    this.scheduleIdleNudge();
    this.inputTextChange.emit(value);
    this.adjustInputHeight();
  }

  submit(): void {
    const text = this.draftValue.trim();
    if (!text || this.isStreaming || !this.assistantAvailable) return;
    this.sendMessage.emit(text);
  }

  submitWithChip(chip: ChatSuggestion): void {
    if (this.isStreaming) return;

    if (chip.guidedMode) {
      this.chipSelected.emit(chip);
      return;
    }

    this.draftValue = chip.prompt;
    this.inputTextChange.emit(chip.prompt);
    this.adjustInputHeight();
    this.focusInput();
  }

  onEnter(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.shiftKey) return;
    event.preventDefault();
    this.submit();
  }

  markHelpful(messageId: string): void {
    this.feedback.emit({ messageId, sentiment: 'helpful' });
  }

  markNotHelpful(messageId: string): void {
    this.feedback.emit({ messageId, sentiment: 'not_helpful' });
  }

  useDraftFor(messageId: string): void {
    this.useDraft.emit(messageId);
  }

  refineDraftFor(messageId: string): void {
    this.refineDraft.emit(messageId);
  }

  joinCommunityFor(messageId: string): void {
    this.joinCommunity.emit(messageId);
  }

  showMoreResultsFor(messageId: string): void {
    this.showMoreResults.emit(messageId);
  }

  shouldShowContextualActions(message: ChatMessage): boolean {
    return !message.feedbackSubmitted &&
      message.id === this.contextualActionMessageId &&
      this.contextualActions.length > 0;
  }

  executeAction(action: ChatAction, messageId: string): void {
    if (action.kind === 'use_draft') {
      this.useDraft.emit(messageId);
      return;
    }

    if (action.kind === 'navigate') {
      this.navigateAction.emit(action);
      return;
    }

    if (action.prompt) {
      this.chipSelected.emit(action.prompt);
    }
  }

  dismissGuidedMode(): void {
    this.clearGuidedMode.emit();
  }

  startNewChat(): void {
    this.showIdleNudge = false;
    this.newChat.emit();
  }

  retryLastMessage(): void {
    this.retry.emit();
  }

  async copyAssistantText(message: ChatMessage): Promise<void> {
    const text = this.visibleAssistantText(message) || message.text;
    if (!text.trim()) return;

    try {
      await navigator.clipboard?.writeText(text);
      this.copiedMessageId = message.id;
      setTimeout(() => {
        if (this.copiedMessageId === message.id) {
          this.copiedMessageId = null;
        }
      }, 1600);
    } catch {
      this.copiedMessageId = null;
    }
  }

  shouldShowExpandedSuggestions(): boolean {
    if (
      !this.chips.length ||
      this.guidedMode ||
      !this.assistantAvailable ||
      this.isStreaming
    ) {
      return false;
    }

    if (this.isEmptyConversationState()) {
      return true;
    }

    return !this.isTyping() && this.suggestionsExpanded;
  }

  guidedModeLabel(): string {
    switch (this.guidedMode) {
      case 'search_posts_guided':
        return 'Search mode';
      case 'rewrite_post_guided':
        return 'Rewrite mode';
      case 'find_community_guided':
        return 'Community mode';
      default:
        return '';
    }
  }

  visibleAssistantText(message: ChatMessage): string {
    const text = message.text?.trim() ?? '';
    if (!text) return '';

    if (message.structured?.kind === 'community') {
      return '';
    }

    if (message.structured?.kind !== 'search_posts') {
      return text;
    }

    const introLines: string[] = [];

    for (const rawLine of text.split('\n')) {
      const line = rawLine.trim();
      if (!line) {
        if (introLines.length) {
          introLines.push('');
        }
        continue;
      }

      if (/^[-*]\s*[^:\n]+:\s+.+$/.test(line)) {
        break;
      }

      introLines.push(line);
    }

    return introLines.join('\n').trim();
  }

  hasVisibleAssistantText(message: ChatMessage): boolean {
    return this.visibleAssistantText(message).length > 0;
  }

  isEmotionalSupportMessage(message: ChatMessage): boolean {
    return message.role === 'assistant' && message.useCase === 'emotional_support' && !message.crisis;
  }

  private scrollToBottom(): void {
    const el = this.messagesEl?.nativeElement;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }

  private adjustInputHeight(): void {
    const el = this.inputEl?.nativeElement;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 80)}px`;
  }

  private focusInput(): void {
    if (this.isMobileViewport()) return;
    this.inputEl?.nativeElement.focus();
  }

  private scheduleIdleNudge(): void {
    this.clearIdleNudgeTimer();
    this.showIdleNudge = false;

    if (
      !this.assistantAvailable ||
      this.isStreaming ||
      this.guidedMode ||
      !this.chips.length ||
      !this.isEmptyConversationState() ||
      this.isTyping()
    ) {
      return;
    }

    this.idleNudgeTimer = setTimeout(() => {
      if (
        this.assistantAvailable &&
        !this.isStreaming &&
        !this.guidedMode &&
        this.chips.length > 0 &&
        this.isEmptyConversationState() &&
        !this.isTyping()
      ) {
        this.showIdleNudge = true;
      }
    }, 4500);
  }

  private clearIdleNudgeTimer(): void {
    if (this.idleNudgeTimer) {
      clearTimeout(this.idleNudgeTimer);
      this.idleNudgeTimer = undefined;
    }
  }

  private syncSuggestionVisibility(): void {
    this.suggestionsExpanded = true;
  }

  private isEmptyConversationState(): boolean {
    return this.messages.length === 1 && this.messages[0]?.role === 'assistant';
  }

  private isTyping(): boolean {
    return this.draftValue.trim().length > 0;
  }

  private isMobileViewport(): boolean {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 479px)').matches;
  }

  private getLatestAssistantMessage(): ChatMessage | null {
    for (let index = this.messages.length - 1; index >= 0; index -= 1) {
      const message = this.messages[index];
      if (message?.role === 'assistant' && !message.crisis) {
        return message;
      }
    }

    return null;
  }
}
